/**
 * TechLayout - Sidebar-Navigation für Techniker-Bereich
 * 
 * PR-NAV: Navigation aus config/navigation.ts (Single Source of Truth)
 * 
 * RBAC:
 * - allowed: TECHNICIAN, ADMIN, SUPERADMIN, SYSTEM
 * - denied: WORKER, FOREMAN → Redirect zu /
 * - Cross-Nav zu /admin: nur ADMIN/SUPERADMIN/SYSTEM (nie TECHNICIAN)
 * 
 * Responsive Layout:
 * - Desktop (≥1024px): Sidebar-Navigation
 * - Mobile (<1024px): Header + Bottom-Navigation
 */

import { ReactNode } from 'react';
import { Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { Home, Wrench } from 'lucide-react';
import { useAuth } from '../App';
import { UserMenu } from './UserMenu';

import { ModuleBadge } from './ModuleBadge';
import { useModules } from '../hooks/useModules';
import { AppFooter } from './AppFooter';
import {
  TECH_NAV_ITEMS,
  CROSS_NAV_ITEMS,
  ICON_MAP,
  filterNavItems,
  type UserRole,
} from '../config/navigation';

// Rollen die TechLayout nutzen dürfen
const ALLOWED_ROLES: UserRole[] = ['TECHNICIAN', 'ADMIN', 'SUPERADMIN', 'SYSTEM'];

interface TechLayoutProps {
  children: ReactNode;
}

export function TechLayout({ children }: TechLayoutProps) {
  const { user, refresh } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { isEnabled } = useModules();

  // RBAC: Rollen-Check - WORKER/FOREMAN werden redirected
  if (!user || !ALLOWED_ROLES.includes(user.role as UserRole)) {
    return <Navigate to="/" replace />;
  }

  const userRole = user.role as UserRole;

  const isActivePath = (href: string) => {
    if (href === '/tech') {
      return location.pathname === '/tech';
    }
    return location.pathname.startsWith(href);
  };

  // Navigation filtern (aus navigation.ts)
  const filteredNavItems = filterNavItems(TECH_NAV_ITEMS, userRole, isEnabled);
  
  // Cross-Nav prüfen
  const crossNavToAdmin = CROSS_NAV_ITEMS.techToAdmin;
  const showCrossNavToAdmin = crossNavToAdmin.rolesAllowed.includes(userRole);

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col lg:flex-row">
      {/* Desktop Sidebar - nur ab lg sichtbar */}
      <aside data-testid="tech-sidebar" className="hidden lg:flex w-64 bg-white shadow-lg flex-col h-screen fixed left-0 top-0">
        {/* Header */}
        <div className="p-4 border-b shrink-0">
          <Link
            to="/"
            className="flex items-center gap-2 text-slate-500 hover:text-slate-700 text-sm mb-2"
          >
            <Home className="w-4 h-4" />
            ← Zur Startseite
          </Link>
          <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Wrench className="w-5 h-5" />
            Techniker-Bereich
          </h1>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 overflow-y-auto">
          <div>
            <h2 className="px-3 mb-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">
              Aufgaben
            </h2>
            <div className="space-y-1">
              {filteredNavItems.map((item) => {
                const Icon = ICON_MAP[item.iconKey];
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                      isActivePath(item.href)
                        ? 'bg-slate-100 text-slate-900 font-medium'
                        : 'text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <Icon className={`w-5 h-5 ${
                      isActivePath(item.href) ? 'text-slate-700' : 'text-slate-500'
                    }`} />
                    <span className="flex items-center gap-2">
                      {item.label}
                      {item.moduleKey && <ModuleBadge module={item.moduleKey} />}
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </nav>

        {/* Footer - Cross-Navigation */}
        {showCrossNavToAdmin && (
          <div className="p-4 border-t shrink-0">
            {(() => {
              const Icon = ICON_MAP[crossNavToAdmin.iconKey];
              return (
                <Link
                  to={crossNavToAdmin.href}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-lg transition-colors"
                >
                  <Icon className="w-4 h-4" />
                  <span>{crossNavToAdmin.label}</span>
                </Link>
              );
            })()}
          </div>
        )}
      </aside>

      {/* Mobile Header - nur unter lg sichtbar */}
      <header className="lg:hidden bg-white shadow-sm sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 py-3">
          <h1 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <Wrench className="w-5 h-5" />
            Techniker-Bereich
          </h1>
          <UserMenu />
        </div>
      </header>

      {/* Hauptbereich */}
      <main data-testid="tech-main-content" className="flex-1 lg:ml-64 flex flex-col">
        {/* Desktop Top-Bar mit UserMenu */}
        <div className="hidden lg:flex items-center justify-end h-14 px-8 bg-white border-b">
          <UserMenu />
        </div>
        <div className="flex-1 p-4 pb-28 lg:p-8 lg:pb-16">
          {children}
        </div>
        <AppFooter position="fixed inset-x-0 bottom-16 z-40 lg:bottom-0 lg:left-64" />
      </main>

      {/* Mobile Bottom Navigation - nur unter lg sichtbar */}
      <nav data-testid="tech-mobile-nav" className="lg:hidden fixed bottom-0 inset-x-0 bg-white border-t z-40">
        <div className="flex justify-around items-center h-16">
          {filteredNavItems.map((item) => {
            const Icon = ICON_MAP[item.iconKey];
            return (
              <Link
                key={item.href}
                to={item.href}
                className={`flex flex-col items-center justify-center min-w-[64px] py-1 ${
                  isActivePath(item.href)
                    ? 'text-blue-600'
                    : 'text-slate-600 hover:text-blue-600'
                }`}
              >
                <Icon className="w-6 h-6" />
                <span className="text-xs mt-1">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
